import {
  AuditOutlined,
  CheckOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import React from 'react';
import {
  confirmFamsAiActionDraft,
  createFamsAiActionDraft,
  updateFamsAiActionDraft,
} from '@/services/bff/ai-assistant.api';
import type {
  FamsAiActionDraft,
  FamsAiActionProposal,
} from '@/services/bff/types/ai-assistant';

const ACTION_LABELS: Record<string, string> = {
  due_diligence_task: '尽调任务',
  research_questions: '研究核查',
  risk_followup: '风险跟进',
  report_draft: '报告草稿',
  data_remediation: '数据修复',
};

const TARGET_LABELS: Record<string, string> = {
  manager: '管理人',
  investment_manager: '投资经理',
  due_diligence: '尽调任务',
  product: '产品',
  portfolio: '组合',
  risk: '风险事项',
};

type FormValues = {
  title: string;
  description?: string;
  owner?: string;
  dueDate?: Dayjs | null;
  priority: number;
};

export const AiActionReviewModal: React.FC<{
  open: boolean;
  proposal?: FamsAiActionProposal | null;
  draft?: FamsAiActionDraft | null;
  onClose: () => void;
  onChanged?: () => void;
}> = ({ open, proposal, draft, onClose, onChanged }) => {
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = React.useState(false);
  const source = draft || proposal;

  React.useEffect(() => {
    if (!open || !source) return;
    form.setFieldsValue({
      title: source.title,
      description: source.description,
      owner: source.owner,
      dueDate: source.dueDate ? dayjs(source.dueDate) : null,
      priority: source.priority || 2,
    });
  }, [form, open, source]);

  const persist = async (confirm: boolean) => {
    if (!source) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      const changes = {
        title: values.title.trim(),
        description: values.description?.trim(),
        owner: values.owner?.trim(),
        dueDate: values.dueDate?.format('YYYY-MM-DD'),
        priority: values.priority,
      };
      const saved = draft
        ? await updateFamsAiActionDraft(draft.uuid, changes)
        : await createFamsAiActionDraft({ ...source, ...changes });
      if (confirm) {
        await confirmFamsAiActionDraft(saved.uuid);
        message.success(
          saved.actionType === 'due_diligence_task'
            ? '已确认并创建尽调任务'
            : '已确认并加入 AI 工作队列',
        );
      } else {
        message.success('已保存到待确认工作队列');
      }
      onChanged?.();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      className="fams-ai-action-modal"
      width={640}
      open={open}
      title={
        <Space>
          <span className="fams-ai-action-modal-icon">
            <AuditOutlined />
          </span>
          <span>
            <Typography.Text strong>审核 AI 建议动作</Typography.Text>
            <Typography.Text
              type="secondary"
              className="fams-ai-action-modal-subtitle"
            >
              确认前可修改，不会自动变更正式业务数据
            </Typography.Text>
          </span>
        </Space>
      }
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button loading={saving} onClick={() => void persist(false)}>
            保存为待确认
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={saving}
            onClick={() => void persist(true)}
          >
            确认并执行
          </Button>
        </Space>
      }
    >
      {source ? (
        <>
          <div className="fams-ai-action-scope">
            <div>
              <Typography.Text type="secondary">动作类型</Typography.Text>
              <strong>
                {ACTION_LABELS[source.actionType] || source.actionType}
              </strong>
            </div>
            <div>
              <Typography.Text type="secondary">影响对象</Typography.Text>
              <strong>
                {TARGET_LABELS[source.targetType] || source.targetType} #
                {source.targetId}
              </strong>
            </div>
            <div>
              <Typography.Text type="secondary">依据</Typography.Text>
              <strong>{source.evidenceIds?.length || 0} 项证据</strong>
            </div>
          </div>
          <Alert
            type="info"
            showIcon
            icon={<FileProtectOutlined />}
            message="系统将在确认时重新校验当前租户、对象归属和字段完整性。审批、交易、估值和主数据不会由 AI 自动修改。"
          />
          <Form form={form} layout="vertical" className="fams-ai-action-form">
            <Form.Item
              name="title"
              label="动作标题"
              rules={[{ required: true, message: '请输入动作标题' }]}
            >
              <Input maxLength={500} />
            </Form.Item>
            <Form.Item name="description" label="核查内容与依据">
              <Input.TextArea rows={6} maxLength={10000} showCount />
            </Form.Item>
            <div className="fams-ai-action-form-grid">
              <Form.Item name="owner" label="负责人">
                <Input maxLength={100} placeholder="可稍后分配" />
              </Form.Item>
              <Form.Item name="dueDate" label="计划完成日">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="priority" label="优先级">
                <Select
                  options={[
                    { label: '高', value: 1 },
                    { label: '中', value: 2 },
                    { label: '低', value: 3 },
                  ]}
                />
              </Form.Item>
            </div>
          </Form>
          <Space wrap size={[6, 6]}>
            {(source.evidenceIds || []).map((id) => (
              <Tag key={id}>{id}</Tag>
            ))}
          </Space>
        </>
      ) : null}
    </Modal>
  );
};
